import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Settings as SettingsIcon, Percent, Plus, Trash2, Save, Building2, DollarSign, Download, CreditCard, Check, ExternalLink, AlertCircle, FileText, FileSpreadsheet, Database, ChevronDown, Users, Copy, Mail, ShieldCheck, Image as ImageIcon, Upload, RefreshCw, Sparkles, MessageSquare, X, KeyRound, Palette, Receipt, Lock, Info } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { useTheme } from "@/context/ThemeContext";
import { useAuth } from "@/context/AuthContext";
import LogoCropDialog from "@/components/LogoCropDialog";
import ResetPasswordDialog from "@/components/ResetPasswordDialog";
import ConnectProviderDialog from "@/components/ConnectProviderDialog";
import FinancialsPinCard from "@/components/FinancialsPinCard";

const BASE_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Payment Provider Logos/Icons
const SquareLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M21 3H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H4c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h16c.55 0 1 .45 1 1v12c0 .55-.45 1-1 1z"/>
  </svg>
);

const StripeLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M13.976 9.15c-2.172-.806-3.356-1.426-3.356-2.409 0-.831.683-1.305 1.901-1.305 2.227 0 4.515.858 6.09 1.631l.89-5.494C18.252.975 15.697 0 12.165 0 9.667 0 7.589.654 6.104 1.872 4.56 3.147 3.757 4.992 3.757 7.218c0 4.039 2.467 5.76 6.476 7.219 2.585.92 3.445 1.574 3.445 2.583 0 .98-.84 1.545-2.354 1.545-1.875 0-4.965-.921-6.99-2.109l-.9 5.555C5.175 22.99 8.385 24 11.714 24c2.641 0 4.843-.624 6.328-1.813 1.664-1.305 2.525-3.236 2.525-5.732 0-4.128-2.524-5.851-6.591-7.305z"/>
  </svg>
);

const PayPalLogo = () => (
  <svg viewBox="0 0 24 24" className="w-6 h-6" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.59 3.025-2.566 6.082-8.558 6.082h-2.19c-1.717 0-3.146 1.27-3.4 2.942l-1.122 7.105a.641.641 0 0 0 .633.74h3.36c.523 0 .967-.382 1.05-.9l.807-5.102a1.056 1.056 0 0 1 1.05-.9h.665c4.298 0 7.665-1.746 8.647-6.797.203-1.04.236-1.883-.294-2.883z"/>
  </svg>
);

export default function Settings({ mode = "all" }) {
  // mode="all"      → all sections except Payments (Payments moved to its own page)
  // mode="payments" → only Payments section (used by the new /payments route)
  const { theme } = useTheme();
  const { isSuperAdmin } = useAuth();
  const { businessSlug } = useParams();
  const isDark = theme === "dark";
  const [taxRates, setTaxRates] = useState([]);
  // Per-business line-item type labels (Part/Labor → Material/Service, etc.)
  // Saved keys stay the same so existing invoices keep working.
  const [lineItemTypes, setLineItemTypes] = useState([]);
  // Collapse the "How it works" guide by default — most owners only
  // need to read it once. Persist the choice locally so it stays
  // collapsed on subsequent visits.
  const [lineItemHelpOpen, setLineItemHelpOpen] = useState(() => {
    try { return localStorage.getItem('lineItemHelpOpen') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('lineItemHelpOpen', lineItemHelpOpen ? '1' : '0'); } catch { /* private mode */ }
  }, [lineItemHelpOpen]);
  const [savingLineItemTypes, setSavingLineItemTypes] = useState(false);
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [businessInfo, setBusinessInfo] = useState({
    name: "Business",
    address: "",
    phone: "",
    logo_url: "",
  });
  const [logoBusy, setLogoBusy] = useState(false); // 'upload' | 'regen' | 'restore' | false
  // Last N archived logos for one-click restore.
  const [logoHistory, setLogoHistory] = useState([]);
  // --- LOGO STAGING ---
  // When the user picks a new logo (upload OR generate OR restore) we stage it
  // here first so they can see the result and decide to Save (commit to DB
  // + propagate everywhere) or Cancel (revert preview to last committed).
  // shape: { dataUrl, source: 'upload'|'ai'|'restore', meta?: any } | null
  const [pendingLogo, setPendingLogo] = useState(null);
  // Raw uploaded image waiting for the crop dialog.
  const [cropSource, setCropSource] = useState(null);
  const [savingLogo, setSavingLogo] = useState(false);
  // Reset-password dialog target (super admin only)
  const [resetPwTarget, setResetPwTarget] = useState(null);
  // Which payment provider's Connect dialog is open (null = none).
  const [connectingProvider, setConnectingProvider] = useState(null);
  // SMS / Twilio status + usage for the Text Messages card.
  const [smsStats, setSmsStats] = useState(null);
  const [zelleInfo, setZelleInfo] = useState({
    zelle_email: "",
    zelle_phone: "",
    zelle_qr_code: ""
  });
  const [markup, setMarkup] = useState({
    type: "amount",
    value: 30
  });
  // Tax Settings
  const [taxSettings, setTaxSettings] = useState({
    tax_labor: false, // Default: don't tax labor (Maryland style)
    default_tax_rate: 7.5,
    business_state: "" // State for auto-configuring tax rules
  });

  // US States with labor tax rules and default tax rates
  // taxLabor: true = labor is taxed, false = labor is NOT taxed
  // taxRate: average state + local sales tax rate
  const US_STATES_TAX_LABOR = {
    'AL': { name: 'Alabama', taxLabor: true, taxRate: 9.24 },
    'AK': { name: 'Alaska', taxLabor: false, taxRate: 1.76 },  // No state tax, local only
    'AZ': { name: 'Arizona', taxLabor: true, taxRate: 8.40 },
    'AR': { name: 'Arkansas', taxLabor: true, taxRate: 9.47 },
    'CA': { name: 'California', taxLabor: false, taxRate: 8.68 },
    'CO': { name: 'Colorado', taxLabor: false, taxRate: 7.77 },
    'CT': { name: 'Connecticut', taxLabor: true, taxRate: 6.35 },
    'DE': { name: 'Delaware', taxLabor: false, taxRate: 0 },  // No sales tax
    'FL': { name: 'Florida', taxLabor: false, taxRate: 7.02 },
    'GA': { name: 'Georgia', taxLabor: false, taxRate: 7.35 },
    'HI': { name: 'Hawaii', taxLabor: true, taxRate: 4.44 },
    'ID': { name: 'Idaho', taxLabor: false, taxRate: 6.02 },
    'IL': { name: 'Illinois', taxLabor: false, taxRate: 8.81 },
    'IN': { name: 'Indiana', taxLabor: false, taxRate: 7.00 },
    'IA': { name: 'Iowa', taxLabor: true, taxRate: 6.94 },
    'KS': { name: 'Kansas', taxLabor: true, taxRate: 8.70 },
    'KY': { name: 'Kentucky', taxLabor: false, taxRate: 6.00 },
    'LA': { name: 'Louisiana', taxLabor: true, taxRate: 9.55 },
    'ME': { name: 'Maine', taxLabor: false, taxRate: 5.50 },
    'MD': { name: 'Maryland', taxLabor: false, taxRate: 6.00 },
    'MA': { name: 'Massachusetts', taxLabor: false, taxRate: 6.25 },
    'MI': { name: 'Michigan', taxLabor: false, taxRate: 6.00 },
    'MN': { name: 'Minnesota', taxLabor: true, taxRate: 7.49 },
    'MS': { name: 'Mississippi', taxLabor: true, taxRate: 7.07 },
    'MO': { name: 'Missouri', taxLabor: false, taxRate: 8.29 },
    'MT': { name: 'Montana', taxLabor: false, taxRate: 0 },  // No sales tax
    'NE': { name: 'Nebraska', taxLabor: true, taxRate: 6.94 },
    'NV': { name: 'Nevada', taxLabor: false, taxRate: 8.23 },
    'NH': { name: 'New Hampshire', taxLabor: false, taxRate: 0 },  // No sales tax
    'NJ': { name: 'New Jersey', taxLabor: true, taxRate: 6.60 },
    'NM': { name: 'New Mexico', taxLabor: true, taxRate: 7.72 },
    'NY': { name: 'New York', taxLabor: true, taxRate: 8.52 },
    'NC': { name: 'North Carolina', taxLabor: false, taxRate: 6.98 },
    'ND': { name: 'North Dakota', taxLabor: false, taxRate: 6.96 },
    'OH': { name: 'Ohio', taxLabor: true, taxRate: 7.24 },
    'OK': { name: 'Oklahoma', taxLabor: false, taxRate: 8.98 },
    'OR': { name: 'Oregon', taxLabor: false, taxRate: 0 },  // No sales tax
    'PA': { name: 'Pennsylvania', taxLabor: false, taxRate: 6.34 },
    'RI': { name: 'Rhode Island', taxLabor: false, taxRate: 7.00 },
    'SC': { name: 'South Carolina', taxLabor: false, taxRate: 7.43 },
    'SD': { name: 'South Dakota', taxLabor: true, taxRate: 6.40 },
    'TN': { name: 'Tennessee', taxLabor: false, taxRate: 9.55 },
    'TX': { name: 'Texas', taxLabor: true, taxRate: 8.20 },
    'UT': { name: 'Utah', taxLabor: false, taxRate: 7.19 },
    'VT': { name: 'Vermont', taxLabor: false, taxRate: 6.24 },
    'VA': { name: 'Virginia', taxLabor: false, taxRate: 5.75 },
    'WA': { name: 'Washington', taxLabor: true, taxRate: 9.29 },
    'WV': { name: 'West Virginia', taxLabor: true, taxRate: 6.55 },
    'WI': { name: 'Wisconsin', taxLabor: false, taxRate: 5.43 },
    'WY': { name: 'Wyoming', taxLabor: true, taxRate: 5.36 },
    'DC': { name: 'Washington D.C.', taxLabor: false, taxRate: 6.00 }
  };

  // Handle state selection - automatically update tax labor setting and tax rate
  // When the user picks a state, we now do NOT prefill any % rates.
  // Tax rates change every year and vary by city/county — we don't
  // want to be the source of a wrong rate that gets the customer
  // audited. We only remember the state itself + the (binary, legal)
  // fact of whether labor is taxable in that state, so the rest of
  // the app can show the right fields. The customer enters their
  // OWN rates below.
  const handleStateChange = (stateCode) => {
    const stateInfo = US_STATES_TAX_LABOR[stateCode];
    if (stateInfo) {
      setTaxSettings(prev => ({
        ...prev,
        business_state: stateCode,
        tax_labor: stateInfo.taxLabor,
        // Don't touch default_tax_rate or labor_tax_rate — customer
        // owns those values.
      }));
      toast.info(`Saved: ${stateInfo.name}. ${stateInfo.taxLabor ? 'Labor is generally taxable here.' : 'Labor is generally NOT taxable here.'} Please enter your own current sales-tax rate below.`);
    }
  };

  // Payment Providers State
  const [paymentProviders, setPaymentProviders] = useState({
    square: {
      enabled: false,
      connected: false,
      application_id: "",
      access_token: "",
      location_id: "",
      environment: "sandbox"
    },
    stripe: {
      enabled: false,
      connected: false,
      publishable_key: "",
      secret_key: ""
    },
    paypal: {
      enabled: false,
      connected: false,
      client_id: "",
      client_secret: "",
      environment: "sandbox"
    }
  });
  const [selectedProvider, setSelectedProvider] = useState(null);
  const [newRate, setNewRate] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMarkup, setSavingMarkup] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [categoryInfo, setCategoryInfo] = useState({ category: "", category_name: "" });
  
  const API = `${BASE_API}/b/${businessSlug}`;

  useEffect(() => {
    if (businessSlug) {
      fetchSettings();
    }
  }, [businessSlug]);

  // Super-admin-only: pull the list of users belonging to this business
  useEffect(() => {
    if (!isSuperAdmin || !businessSlug) return;
    let cancelled = false;
    (async () => {
      setAdminUsersLoading(true);
      try {
        const res = await axios.get(`${BASE_API}/admin/businesses/by-slug/${businessSlug}/users`);
        if (!cancelled) {
          setAdminUsers(res.data?.users || []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch business users:", err);
          toast.error("Could not load account owners");
        }
      } finally {
        if (!cancelled) setAdminUsersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [businessSlug, isSuperAdmin]);

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied`);
    }).catch(() => {
      toast.error("Could not copy");
    });
  };

  const formatDate = (val) => {
    if (!val) return "—";
    try {
      return new Date(val).toLocaleString();
    } catch {
      return String(val);
    }
  };

  const ROLE_STYLES = {
    super_admin: { label: "Super Admin", color: "text-fuchsia-400", bg: "bg-fuchsia-500/10" },
    owner: { label: "Owner", color: "text-amber-400", bg: "bg-amber-500/10" },
    manager: { label: "Manager", color: "text-cyan-400", bg: "bg-cyan-500/10" },
    employee: { label: "Employee", color: "text-emerald-400", bg: "bg-emerald-500/10" },
  };

  const fetchSettings = async () => {
    try {
      const [response, lineTypesRes] = await Promise.all([
        axios.get(`${API}/settings`),
        axios.get(`${API}/line-item-types`),
      ]);
      // Normalize defaults: even if legacy data has multiple `is_default:true`
      // rows, only honor the FIRST one (React radio buttons go visually
      // broken if more than one is checked).
      const rawTypes = lineTypesRes.data.line_item_types || [];
      let foundDefault = false;
      const normalized = rawTypes.map((t) => {
        if (t.is_default && !foundDefault) {
          foundDefault = true;
          return { ...t, is_default: true };
        }
        return { ...t, is_default: false };
      });
      if (normalized.length && !foundDefault) normalized[0].is_default = true;
      setLineItemTypes(normalized);
      // Kick off logo-history fetch in parallel — non-fatal if it fails.
      fetchLogoHistory();
      // Kick off SMS stats fetch — same non-fatal pattern.
      axios.get(`${API}/sms/stats`).then(r => setSmsStats(r.data)).catch(() => {});
      if (response.data.tax_rates) {
        setTaxRates(response.data.tax_rates);
      }
      if (response.data.business_info) {
        setBusinessInfo({
          name: response.data.business_info.name || "Business",
          address: response.data.business_info.address || "",
          phone: response.data.business_info.phone || "",
          logo_url: response.data.business_info.logo_url || response.data.logo_url || "",
        });
      }
      if (response.data.zelle_info) {
        setZelleInfo(response.data.zelle_info);
      } else if (response.data.business_info?.zelle_email) {
        setZelleInfo({
          zelle_email: response.data.business_info.zelle_email || "",
          zelle_phone: ""
        });
      }
      if (response.data.markup) {
        setMarkup(response.data.markup);
      }
      if (response.data.tax_settings) {
        setTaxSettings(response.data.tax_settings);
      }
      // Store category info
      if (response.data.category) {
        setCategoryInfo({
          category: response.data.category,
          category_name: response.data.category_name || response.data.category
        });
      }
      if (response.data.payment_providers) {
        setPaymentProviders(prev => ({
          ...prev,
          ...response.data.payment_providers
        }));
      }
    } catch (error) {
      setTaxRates([0, 6, 7.5, 8, 8.25, 10]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddRate = () => {
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0 || rate > 100) {
      toast.error("Please enter a valid tax rate (0-100)");
      return;
    }
    if (taxRates.includes(rate)) {
      toast.error("This rate already exists");
      return;
    }
    setTaxRates([...taxRates, rate].sort((a, b) => a - b));
    setNewRate("");
  };

  const handleRemoveRate = (rate) => {
    setTaxRates(taxRates.filter(r => r !== rate));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/settings`, {
        tax_rates: taxRates,
        tax_settings: taxSettings,
        business_info: businessInfo,
        zelle_info: zelleInfo,
        payment_providers: paymentProviders,
        skip_markup_update: true  // Don't update inventory prices
      });
      toast.success("Settings saved!");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  // Callback for the ConnectProviderDialog. Receives validated creds and
  // mode ("live"|"test"|"sandbox"), updates the local state, AND persists
  // via the existing /settings POST so it's live immediately.
  const handleProviderConnected = async (creds, mode) => {
    if (!connectingProvider) return;
    const nextProviders = {
      ...paymentProviders,
      [connectingProvider]: {
        ...paymentProviders[connectingProvider],
        ...creds,
        enabled: true,
        connected: true,
        mode: mode || "live",
      },
    };
    setPaymentProviders(nextProviders);
    try {
      await axios.post(`${API}/settings`, {
        tax_rates: taxRates,
        tax_settings: taxSettings,
        business_info: businessInfo,
        zelle_info: zelleInfo,
        payment_providers: nextProviders,
        skip_markup_update: true,
      });
      toast.success(`${connectingProvider.charAt(0).toUpperCase()}${connectingProvider.slice(1)} connected.`);
    } catch (err) {
      toast.error("Saved keys locally but failed to persist. Please retry.");
      throw err;
    }
  };

  // Disconnect a provider — clears credentials and toggles off. Asks
  // before doing anything destructive.
  const handleDisconnectProvider = async (provider) => {
    if (!window.confirm(`Disconnect ${provider}? You'll need to re-paste the keys to reconnect.`)) return;
    const blank = {
      square: { enabled: false, connected: false, application_id: "", access_token: "", location_id: "" },
      stripe: { enabled: false, connected: false, publishable_key: "", secret_key: "" },
      paypal: { enabled: false, connected: false, client_id: "", client_secret: "" },
    }[provider];
    const next = { ...paymentProviders, [provider]: blank };
    setPaymentProviders(next);
    try {
      await axios.post(`${API}/settings`, {
        tax_rates: taxRates,
        tax_settings: taxSettings,
        business_info: businessInfo,
        zelle_info: zelleInfo,
        payment_providers: next,
        skip_markup_update: true,
      });
      toast.success(`${provider} disconnected.`);
    } catch (err) {
      toast.error("Failed to disconnect.");
    }
  };


  const handleSaveLineItemTypes = async () => {
    setSavingLineItemTypes(true);
    try {
      const payload = lineItemTypes
        .filter((t) => t.label && t.label.trim())
        .map((t, idx) => {
          // Derive kind for backward-compat rows that don't have it yet.
          const kind = t.kind || (t.track_inventory ? "part"
            : (t.key === "labor" || /labor|service|diagnost/i.test(t.label || "")) ? "labor"
            : "item");
          return {
            key: t.key,
            label: t.label.trim(),
            kind,
            track_inventory:
              kind === "part" ? true
              : kind === "labor" ? false
              : !!t.track_inventory,
            // First row is the default-on-new-invoice (we no longer let
            // the user pick this — the user said "it's already selected
            // until the customer adds their own"). The backend still
            // requires exactly one `is_default`.
            is_default: idx === 0,
          };
        });
      if (payload.length === 0) {
        toast.error("Add at least one line-item type.");
        return;
      }
      await axios.post(`${API}/line-item-types`, { line_item_types: payload });
      toast.success("Line-item labels saved!");
    } catch (error) {
      toast.error("Failed to save line-item types");
    } finally {
      setSavingLineItemTypes(false);
    }
  };

  const handleSaveMarkup = async () => {
    setSavingMarkup(true);
    try {
      const response = await axios.post(`${API}/settings/markup`, {
        markup: markup
      });
      const updatedCount = response.data.inventory_updated || 0;
      toast.success(`Markup saved! ${updatedCount} inventory items updated (locked items unchanged).`);
    } catch (error) {
      toast.error("Failed to save markup");
    } finally {
      setSavingMarkup(false);
    }
  };

  const handleDeleteQrCode = () => {
    setZelleInfo({ ...zelleInfo, zelle_qr_code: "" });
    toast.success("QR code removed. Click 'Save Changes' to confirm.");
  };

  // -------------------------- LOGO MANAGEMENT --------------------------
  // We now use a *staged* preview flow so the user always gets a Save / Cancel
  // decision before a change goes live on invoices, the sidebar, etc.
  //   1. Upload  → read file → open crop dialog → stage data URL
  //   2. AI gen  → call backend → stage returned URL (backend already saved,
  //                but we keep it in the previous-history list so Cancel can
  //                roll back via /restore-logo).
  //   3. Restore → identical staged-preview semantics as AI gen.
  // The actual DB commit for an *upload* happens in handleSavePendingLogo;
  // AI gen and restore commit immediately on the server (the API returns the
  // new URL), and Cancel rolls them back by calling /restore-logo index 0.

  // Step 1: file picked → open crop dialog with the raw image.
  const handleUploadLogo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file (PNG, JPG, WebP, SVG).");
      return;
    }
    if (file.size > 1_500_000) {
      toast.error("Image is too large. Please use one under 1.5 MB.");
      return;
    }
    try {
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = () => rej(r.error);
        r.readAsDataURL(file);
      });
      setCropSource(dataUrl);
    } catch {
      toast.error("Could not read that image.");
    }
  };

  // Step 2: cropper confirmed → stage the cropped square. NOT yet persisted.
  const handleCropConfirm = (croppedDataUrl) => {
    setCropSource(null);
    setPendingLogo({ dataUrl: croppedDataUrl, source: "upload" });
  };

  // Step 3: user clicks Save on the staged preview → POST to server.
  const handleSavePendingLogo = async () => {
    if (!pendingLogo) return;
    setSavingLogo(true);
    try {
      if (pendingLogo.source === "upload") {
        const resp = await axios.post(`${API}/upload-logo`, { logo_base64: pendingLogo.dataUrl });
        setBusinessInfo((bi) => ({ ...bi, logo_url: resp.data.logo_url }));
        await fetchLogoHistory();
      } else {
        // AI / restore are already persisted; just sync the preview.
        setBusinessInfo((bi) => ({ ...bi, logo_url: pendingLogo.dataUrl }));
      }
      setPendingLogo(null);
      toast.success("Logo saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save logo.");
    } finally {
      setSavingLogo(false);
    }
  };

  // Cancel: discard the staged preview. For AI/restore we already committed
  // to the server, so we roll back to the previous logo via /restore-logo.
  const handleCancelPendingLogo = async () => {
    if (!pendingLogo) return;
    if (pendingLogo.source === "upload") {
      // Pure client-side discard.
      setPendingLogo(null);
      toast.message("Logo change discarded.");
      return;
    }
    // AI gen or history restore — server side rollback.
    setLogoBusy("restore");
    try {
      const resp = await axios.post(`${API}/restore-logo`, { index: 0 });
      setBusinessInfo((bi) => ({ ...bi, logo_url: resp.data.logo_url }));
      await fetchLogoHistory();
      setPendingLogo(null);
      toast.success("Reverted to previous logo.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert.");
    } finally {
      setLogoBusy(false);
    }
  };

  // Regenerate the logo using AI (gpt-image-1). Takes ~10–20s.
  // Server commits immediately (and pushes the previous to history) — we
  // stage the result so the user can keep it (Save) or roll back (Cancel).
  const handleRegenerateLogo = async () => {
    setLogoBusy("regen");
    try {
      const resp = await axios.post(`${API}/regenerate-logo`);
      await fetchLogoHistory();
      // Stage — don't update businessInfo.logo_url yet, that happens on Save.
      setPendingLogo({ dataUrl: resp.data.logo_url, source: "ai" });
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const status = e?.response?.status;
      // Surface budget / quota errors with a clear, friendly message.
      if (status === 402 || /budget|quota|exceed/i.test(detail)) {
        toast.error(
          "AI logo budget reached on your Universal Key. Top up in Profile → Universal Key, or upload a logo file instead."
        );
      } else {
        toast.error(detail || "Failed to regenerate logo.");
      }
    } finally {
      setLogoBusy(false);
    }
  };

  // Logo history (last 5 archived logos) so the user can one-click revert
  // if the latest AI gen wasn't an improvement.
  const fetchLogoHistory = async () => {
    try {
      const resp = await axios.get(`${API}/logo-history`);
      setLogoHistory(resp.data.history || []);
    } catch (e) { /* non-fatal */ }
  };

  // Stage a previous logo from history. Server commits + history rotates so
  // Cancel still rolls back via /restore-logo index 0.
  const handleRestoreLogo = async (index) => {
    setLogoBusy("restore");
    try {
      const resp = await axios.post(`${API}/restore-logo`, { index });
      await fetchLogoHistory();
      setPendingLogo({ dataUrl: resp.data.logo_url, source: "restore" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to restore logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  const updateProvider = (provider, field, value) => {
    setPaymentProviders(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: value
      }
    }));
  };

  const toggleProvider = (provider) => {
    setPaymentProviders(prev => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        enabled: !prev[provider].enabled
      }
    }));
    if (!paymentProviders[provider].enabled) {
      setSelectedProvider(provider);
    }
  };

  const handleExportData = async () => {
    setExporting(true);
    try {
      // Download PDF report
      const response = await axios.get(`${API}/export/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessSlug}-complete-export-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Complete export downloaded! Includes all customers, inventory, and invoices.");
    } catch (error) {
      toast.error("Failed to export data");
    } finally {
      setExporting(false);
    }
  };

  const handleExportJSON = async () => {
    setExporting(true);
    try {
      // Download JSON backup
      const response = await axios.get(`${API}/export`);
      
      // Create download link
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessSlug}-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("JSON backup downloaded! This contains all your raw data.");
    } catch (error) {
      toast.error("Failed to export JSON backup");
    } finally {
      setExporting(false);
    }
  };

  const handleExportCSV = async (type) => {
    setExporting(true);
    try {
      const response = await axios.get(`${API}/export/csv/${type}`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${businessSlug}-${type}-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} CSV downloaded!`);
    } catch (error) {
      toast.error(`Failed to export ${type} CSV`);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-800'}`}>
            {mode === 'payments' ? 'Payments' : 'Settings'}
          </h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            {mode === 'payments'
              ? 'Connect Stripe, Square, PayPal, and add your Zelle details so customers can pay you directly.'
              : 'Manage your business settings'}
          </p>
        </div>
        <div className="flex gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                disabled={exporting}
                className={isDark ? 'border-slate-600' : 'border-slate-200'}
                data-testid="export-dropdown-btn"
              >
                <Download className="w-4 h-4 mr-2" />
                {exporting ? "Exporting..." : "Export Data"}
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
              <DropdownMenuLabel className={isDark ? 'text-slate-400' : 'text-slate-500'}>Complete Export</DropdownMenuLabel>
              <DropdownMenuItem onClick={handleExportData} className="cursor-pointer">
                <FileText className="w-4 h-4 mr-2 text-red-500" />
                <span>Full Report (PDF)</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportJSON} className="cursor-pointer">
                <Database className="w-4 h-4 mr-2 text-blue-500" />
                <span>Complete Backup (JSON)</span>
              </DropdownMenuItem>
              <DropdownMenuSeparator className={isDark ? 'bg-slate-700' : 'bg-slate-200'} />
              <DropdownMenuLabel className={isDark ? 'text-slate-400' : 'text-slate-500'}>CSV Export</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => handleExportCSV('customers')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-500" />
                <span>Customers (CSV)</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportCSV('inventory')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-500" />
                <span>Inventory (CSV)</span>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExportCSV('invoices')} className="cursor-pointer">
                <FileSpreadsheet className="w-4 h-4 mr-2 text-green-500" />
                <span>Invoices (CSV)</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="save-settings-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>


      {/* Sections stacked vertically — no tab bar per user request.
          Each <section> uses a responsive 2-col grid so cards sit
          side-by-side on wide screens, single column on mobile. */}
      <div className="w-full space-y-6" data-testid="settings-stack">

        {/* ================= GENERAL ================= */}
        {mode !== 'payments' && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0" data-testid="settings-section-general">
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}>
          <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              <Building2 className="w-5 h-5 text-blue-600" />
              Business Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Business Name</Label>
              <Input
                value={businessInfo.name}
                onChange={(e) => setBusinessInfo({ ...businessInfo, name: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="business-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Address</Label>
              <Input
                value={businessInfo.address}
                onChange={(e) => setBusinessInfo({ ...businessInfo, address: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="business-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Phone</Label>
              <Input
                value={businessInfo.phone}
                onChange={(e) => setBusinessInfo({ ...businessInfo, phone: e.target.value })}
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="business-phone-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Line-Item Types — let each business design their own categories.
            Pro layout: card with structured header (count badge + "How it
            works" collapsible), table-like rows with explicit column
            headers, and a sticky save bar at the bottom. */}
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} data-testid="line-item-types-card">
          <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'} pb-4`}>
            <div className="flex items-start justify-between gap-3">
              <div className="space-y-1.5">
                <CardTitle className={`flex items-center gap-2 text-base ${isDark ? 'text-white' : 'text-slate-800'}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-blue-500/15' : 'bg-blue-50'}`}>
                    <FileText className="w-4 h-4 text-blue-600" />
                  </div>
                  Line-Item Categories
                  <span
                    className={`ml-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${isDark ? 'bg-slate-700 text-slate-300' : 'bg-slate-100 text-slate-600'}`}
                    data-testid="line-item-types-count"
                  >
                    {lineItemTypes.length} active
                  </span>
                </CardTitle>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  Configure how each row on an invoice is classified. Affects inventory math, tax, and profit reports.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setLineItemHelpOpen((v) => !v)}
                className={`shrink-0 inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1.5 rounded-lg transition-colors ${isDark ? 'text-slate-300 hover:bg-slate-700' : 'text-slate-600 hover:bg-slate-100'}`}
                data-testid="line-item-help-toggle"
                aria-expanded={lineItemHelpOpen}
              >
                <Info className="w-3.5 h-3.5" />
                {lineItemHelpOpen ? 'Hide guide' : 'How it works'}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${lineItemHelpOpen ? 'rotate-180' : ''}`} />
              </button>
            </div>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            {lineItemHelpOpen && (
              <div
                className={`rounded-xl border p-4 grid sm:grid-cols-3 gap-3 text-xs leading-relaxed animate-in fade-in slide-in-from-top-1 ${isDark ? 'bg-slate-900/40 border-slate-700' : 'bg-slate-50 border-slate-200'}`}
                data-testid="line-item-help-panel"
              >
                {[
                  { dot: 'bg-emerald-500', text: isDark ? 'text-emerald-300' : 'text-emerald-700', name: 'Part', body: 'Tied to your inventory. Selling it decrements stock and counts the cost against profit.' },
                  { dot: 'bg-purple-500', text: isDark ? 'text-purple-300' : 'text-purple-700', name: 'Labor', body: 'A service / time charge. No inventory, 100% margin. State-specific labor tax is pulled from your Tax tab.' },
                  { dot: 'bg-emerald-500', text: isDark ? 'text-emerald-300' : 'text-emerald-700', name: 'Item', body: 'Miscellaneous (customer-supplied part, fee, shop supplies). Toggle inventory tracking per row.' },
                ].map((k) => (
                  <div key={k.name} className={`rounded-lg p-3 ${isDark ? 'bg-slate-800/50' : 'bg-white'} border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className={`w-2 h-2 rounded-full ${k.dot}`} />
                      <span className={`text-[11px] font-bold uppercase tracking-wider ${k.text}`}>{k.name}</span>
                    </div>
                    <p className={isDark ? 'text-slate-400' : 'text-slate-600'}>{k.body}</p>
                  </div>
                ))}
                <p className={`sm:col-span-3 text-[11px] mt-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  <Lock className="w-3 h-3 inline -mt-0.5 mr-1" />
                  <strong>Labor is locked</strong> because tax rules + reports reference it directly. You can rename it (e.g. "Service Call") but it can't be removed or reclassified.
                </p>
              </div>
            )}

            {/* Column header row — clarifies what each control means.
                Mirrors a table-style data grid so the columns line up
                with each editable row below. */}
            <div className={`hidden sm:grid items-center gap-3 px-3 py-2 rounded-lg text-[10px] uppercase tracking-wider font-semibold ${isDark ? 'bg-slate-900/40 text-slate-500' : 'bg-slate-50 text-slate-500'}`}
                 style={{ gridTemplateColumns: '8px 1fr 124px 110px 36px' }}>
              <span />
              <span>Category name</span>
              <span>Type</span>
              <span>Inventory</span>
              <span />
            </div>

            <div className="space-y-2">
              {lineItemTypes.map((t, idx) => {
                // Derive the 3-way "kind" from the stored fields.
                const kind = t.kind || (t.track_inventory ? 'part'
                  : (t.key === 'labor' || /labor|service|diagnost/i.test(t.label || '')) ? 'labor'
                  : 'item');
                const accent = kind === 'part'
                  ? { dot: 'bg-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', text: isDark ? 'text-emerald-300' : 'text-emerald-700', ring: 'ring-emerald-500/20', label: 'Part' }
                  : kind === 'labor'
                  ? { dot: 'bg-purple-500', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50', text: isDark ? 'text-purple-300' : 'text-purple-700', ring: 'ring-purple-500/20', label: 'Labor' }
                  : { dot: 'bg-emerald-500', bg: isDark ? 'bg-emerald-500/10' : 'bg-emerald-50', text: isDark ? 'text-emerald-300' : 'text-emerald-700', ring: 'ring-emerald-500/20', label: 'Item' };
                const isLabor = t.key === 'labor';

                return (
                  <div
                    key={t.key || `row-${idx}`}
                    className={`group relative grid items-center gap-3 px-3 py-2.5 rounded-xl border transition-all hover:shadow-sm ${isDark ? 'bg-slate-700/30 border-slate-700/60 hover:border-slate-600' : 'bg-white border-slate-200 hover:border-slate-300'} ${isLabor ? 'ring-1 ring-inset ' + accent.ring : ''}`}
                    style={{ gridTemplateColumns: '8px 1fr 124px 110px 36px' }}
                    data-testid={`line-item-type-row-${t.key || idx}`}
                  >
                    {/* Accent dot */}
                    <div className={`w-2.5 h-2.5 rounded-full ${accent.dot} shrink-0`} />

                    {/* Label input */}
                    <Input
                      value={t.label}
                      onChange={(e) => {
                        const next = [...lineItemTypes];
                        next[idx] = { ...next[idx], label: e.target.value };
                        setLineItemTypes(next);
                      }}
                      placeholder={kind === 'part' ? 'Part / Tire / Material' : kind === 'labor' ? 'Labor / Diagnostic' : 'Item / Fee / Roadside'}
                      className={`min-w-0 border-0 bg-transparent px-0 focus-visible:ring-0 font-medium ${isDark ? 'text-white placeholder-slate-500' : 'text-slate-800 placeholder-slate-400'}`}
                      data-testid={`line-item-type-input-${t.key || idx}`}
                    />

                    {/* Kind picker — Labor row locked. */}
                    <Select
                      value={kind}
                      onValueChange={(value) => {
                        if (isLabor && value !== 'labor') {
                          toast.error("The Labor category is locked because tax & reports use it. Rename it instead.");
                          return;
                        }
                        const next = [...lineItemTypes];
                        next[idx] = {
                          ...next[idx],
                          kind: value,
                          track_inventory: value === 'part' ? true : value === 'labor' ? false : !!next[idx].track_inventory,
                        };
                        setLineItemTypes(next);
                      }}
                      disabled={isLabor}
                    >
                      <SelectTrigger
                        className={`h-8 text-xs font-semibold rounded-full ${accent.bg} ${accent.text} border-0 ${isLabor ? 'opacity-80 cursor-not-allowed' : ''}`}
                        data-testid={`line-item-type-kind-${t.key || idx}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white'}>
                        <SelectItem value="part">
                          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Part</span>
                        </SelectItem>
                        <SelectItem value="labor">
                          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-purple-500" /> Labor</span>
                        </SelectItem>
                        <SelectItem value="item">
                          <span className="flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> Item</span>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    {/* Inventory pill — Switch-style for items, locked badge
                        for parts, dash for labor. */}
                    {kind === 'labor' ? (
                      <span className={`text-[11px] italic ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>—</span>
                    ) : kind === 'part' ? (
                      <span
                        className={`inline-flex items-center gap-1.5 h-6 px-2 rounded-full text-[10px] font-semibold ${isDark ? 'bg-emerald-500/15 text-emerald-300' : 'bg-emerald-100 text-emerald-700'}`}
                        title="Parts always affect inventory"
                      >
                        <Lock className="w-2.5 h-2.5" /> Tracked
                      </span>
                    ) : (
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={!!t.track_inventory}
                          onCheckedChange={(checked) => {
                            const next = [...lineItemTypes];
                            next[idx] = { ...next[idx], track_inventory: checked };
                            setLineItemTypes(next);
                          }}
                          data-testid={`line-item-type-track-inventory-${t.key || idx}`}
                          aria-label="Track inventory for this category"
                          className="scale-90"
                        />
                        <span className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                          {t.track_inventory ? 'On' : 'Off'}
                        </span>
                      </div>
                    )}

                    {/* Remove / lock indicator */}
                    {!isLabor ? (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (lineItemTypes.length <= 1) {
                            toast.error("You need at least one category.");
                            return;
                          }
                          const next = lineItemTypes.filter((_, i) => i !== idx);
                          if (!next.some((r) => r.is_default)) next[0].is_default = true;
                          setLineItemTypes(next);
                        }}
                        className="w-8 h-8 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity"
                        data-testid={`line-item-type-remove-${t.key || idx}`}
                        title="Remove this category"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    ) : (
                      <span
                        className={`w-8 h-8 inline-flex items-center justify-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        title="Labor is locked because tax & reports reference it"
                        data-testid={`line-item-type-locked-${t.key}`}
                      >
                        <Lock className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Sticky-feeling footer with primary save and secondary add */}
            <div className={`flex flex-col sm:flex-row gap-2 pt-2 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  const existingKeys = new Set(lineItemTypes.map((t) => t.key));
                  let i = 1;
                  let candidate = `custom_${i}`;
                  while (existingKeys.has(candidate)) {
                    i += 1;
                    candidate = `custom_${i}`;
                  }
                  setLineItemTypes([
                    ...lineItemTypes,
                    { key: candidate, label: "", kind: 'item', track_inventory: false, is_default: false },
                  ]);
                }}
                className={`sm:w-44 ${isDark ? 'border-slate-600 text-slate-200' : 'border-slate-300 text-slate-700'}`}
                data-testid="add-line-item-type-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add category
              </Button>
              <div className="flex-1" />
              <Button
                onClick={handleSaveLineItemTypes}
                disabled={savingLineItemTypes}
                className="sm:w-48 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-line-item-types-btn"
              >
                <Save className="w-4 h-4 mr-2" />
                {savingLineItemTypes ? 'Saving…' : 'Save categories'}
              </Button>
            </div>
            <p className={`text-[11px] leading-relaxed flex items-start gap-1.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              <Info className="w-3 h-3 mt-0.5 shrink-0" />
              Renaming a label is safe — existing invoices keep working. Removing a category never deletes past invoice items.
            </p>
          </CardContent>
        </Card>
        </section>
        )}

        {/* ================= BRANDING ================= */}
        {mode !== 'payments' && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0 items-start" data-testid="settings-section-branding">
        <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} data-testid="branding-card">
          <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              <ImageIcon className="w-5 h-5 text-cyan-500" />
              Business Logo
              {pendingLogo && (
                <Badge variant="outline" className="ml-2 text-[10px] border-amber-400 text-amber-500" data-testid="logo-pending-badge">
                  Unsaved change
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-600'}`}>
              Your logo appears on every invoice, the dashboard sidebar, and printable receipts.
              Upload your own (we'll give you crop & zoom controls), generate one with AI, or
              restore a previous version — then click <strong>Save</strong> to make it live.
            </p>

            {/* Side-by-side: CURRENT (saved) vs PREVIEW (staged) */}
            <div className="flex flex-wrap items-center gap-5">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className={`text-[10px] uppercase tracking-wider mb-2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>Current</p>
                  <div
                    className={`w-28 h-28 flex items-center justify-center rounded-xl border-2 ${isDark ? 'border-slate-700 bg-slate-900' : 'border-slate-200 bg-slate-50'} overflow-hidden shrink-0`}
                    data-testid="logo-preview-current"
                  >
                    {businessInfo.logo_url ? (
                      <img src={businessInfo.logo_url} alt={`${businessInfo.name} logo`} className="w-full h-full object-contain" />
                    ) : (
                      <ImageIcon className={`w-10 h-10 ${isDark ? 'text-slate-700' : 'text-slate-300'}`} />
                    )}
                  </div>
                </div>
                {pendingLogo && (
                  <>
                    <ChevronDown className="w-5 h-5 rotate-[-90deg] text-cyan-500 shrink-0" />
                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-wider mb-2 text-cyan-500 font-semibold">New (preview)</p>
                      <div
                        className={`w-28 h-28 flex items-center justify-center rounded-xl border-2 border-cyan-500 ${isDark ? 'bg-slate-900' : 'bg-slate-50'} overflow-hidden shrink-0 shadow-lg shadow-cyan-500/20`}
                        data-testid="logo-preview-pending"
                      >
                        <img src={pendingLogo.dataUrl} alt="New logo preview" className="w-full h-full object-contain" />
                      </div>
                    </div>
                  </>
                )}
              </div>

              <div className="flex-1 min-w-[220px] space-y-3">
                {/* PRIMARY ACTIONS */}
                {pendingLogo ? (
                  <div className="flex flex-wrap gap-2" data-testid="logo-save-cancel-row">
                    <Button
                      type="button"
                      onClick={handleSavePendingLogo}
                      disabled={savingLogo}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="logo-save-btn"
                    >
                      <Check className="w-4 h-4 mr-2" />
                      {savingLogo ? "Saving…" : "Save logo"}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleCancelPendingLogo}
                      disabled={savingLogo || !!logoBusy}
                      className={isDark ? 'border-slate-600 text-slate-200' : 'border-slate-300 text-slate-700'}
                      data-testid="logo-cancel-btn"
                    >
                      <X className="w-4 h-4 mr-2" />
                      Cancel & revert
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
                        className="hidden"
                        onChange={(e) => { handleUploadLogo(e.target.files?.[0]); e.target.value = ""; }}
                        data-testid="logo-upload-input"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        disabled={!!logoBusy}
                        onClick={(e) => e.currentTarget.previousSibling.click()}
                        className={isDark ? 'border-slate-600 text-slate-200' : 'border-slate-300 text-slate-700'}
                        data-testid="logo-upload-btn"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Upload your own
                      </Button>
                    </label>

                    <Button
                      type="button"
                      onClick={handleRegenerateLogo}
                      disabled={!!logoBusy}
                      className="bg-cyan-600 hover:bg-cyan-700 text-white"
                      data-testid="logo-regenerate-btn"
                    >
                      {logoBusy === "regen" ? (
                        <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generating…</>
                      ) : (
                        <><Sparkles className="w-4 h-4 mr-2" /> Generate AI logo</>
                      )}
                    </Button>
                  </div>
                )}

                <div className={`text-xs leading-relaxed space-y-1 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>
                  {pendingLogo?.source === "upload" && (
                    <p><strong className={isDark ? 'text-amber-400' : 'text-amber-600'}>Preview only.</strong> Click <strong>Save logo</strong> to make this live on invoices and the dashboard, or Cancel to discard.</p>
                  )}
                  {pendingLogo?.source === "ai" && (
                    <p><strong className={isDark ? 'text-amber-400' : 'text-amber-600'}>AI logo generated.</strong> Click <strong>Save logo</strong> to keep it, or Cancel to revert to your previous one.</p>
                  )}
                  {pendingLogo?.source === "restore" && (
                    <p><strong className={isDark ? 'text-amber-400' : 'text-amber-600'}>Logo restored from history.</strong> Click <strong>Save logo</strong> to keep it, or Cancel to revert.</p>
                  )}
                  {!pendingLogo && (
                    <>
                      <p><strong className={isDark ? 'text-slate-400' : 'text-slate-600'}>Upload your own:</strong> PNG, JPG, WebP, or SVG up to 1.5 MB. After picking a file you'll get a crop & zoom tool so the logo lands perfectly square.</p>
                      <p><strong className={isDark ? 'text-slate-400' : 'text-slate-600'}>AI generation:</strong> sends your business name + category to gpt-image-1 and returns a clean square logo on white. ~15–30s. If the budget on your Universal Key is exhausted, you'll see a friendly error — top up under Profile → Universal Key.</p>
                    </>
                  )}
                </div>

                {/* PREVIOUS LOGOS — one-click restore. */}
                {logoHistory.length > 0 && !pendingLogo && (
                  <div className="space-y-2" data-testid="logo-history-strip">
                    <p className={`text-xs uppercase tracking-wider font-medium ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      Previous logos
                      <span className={`ml-2 normal-case font-normal ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                        — click to preview & save
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {logoHistory.map((entry, idx) => (
                        <button
                          key={`${entry.archived_at}-${idx}`}
                          type="button"
                          onClick={() => handleRestoreLogo(idx)}
                          disabled={!!logoBusy}
                          className={`group relative w-14 h-14 rounded-lg border-2 overflow-hidden transition-all hover:scale-105 hover:shadow-md ${isDark ? 'bg-white border-slate-600 hover:border-cyan-400' : 'bg-white border-slate-200 hover:border-cyan-500'} disabled:opacity-50`}
                          title={`Restore logo archived ${new Date(entry.archived_at).toLocaleDateString()}`}
                          data-testid={`logo-history-item-${idx}`}
                        >
                          <img src={entry.logo_url} alt={`Previous logo ${idx + 1}`} className="w-full h-full object-contain p-1" />
                          <span className="absolute inset-0 flex items-center justify-center bg-black/60 text-white text-[10px] font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                            Preview
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
          {/* PIN card sits in the right column of the Branding row so
              the two cards balance side-by-side instead of leaving
              awkward empty space. */}
          <FinancialsPinCard businessSlug={businessSlug} isDark={isDark} />
        </section>
        )}

        {/* ================= PAYMENTS ================= */}
        {mode === 'payments' && (
        <section className="flex flex-col gap-6 mt-0" data-testid="settings-section-payments">
          {/* Payment Providers FIRST — the user explicitly asked for the
              real "banking" connection cards (Square/Stripe/PayPal) above
              the informational Zelle block, since that's where the money
              actually moves. We reorder via CSS `order-*` so the JSX
              blocks stay in their original positions (less diff churn). */}
      <Card className={`order-2 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
              <DollarSign className="w-5 h-5 text-purple-600" />
              Zelle Payment Info
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Add your Zelle details so customers can pay you directly.
            </p>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Zelle Email</Label>
              <Input
                type="email"
                value={zelleInfo.zelle_email}
                onChange={(e) => setZelleInfo({ ...zelleInfo, zelle_email: e.target.value })}
                placeholder="your-zelle@email.com"
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="zelle-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Zelle Phone Number</Label>
              <Input
                type="tel"
                value={zelleInfo.zelle_phone}
                onChange={(e) => setZelleInfo({ ...zelleInfo, zelle_phone: e.target.value })}
                placeholder="(555) 123-4567"
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="zelle-phone-input"
              />
            </div>
            <div className="space-y-2">
              <Label className={isDark ? 'text-slate-300' : 'text-slate-700'}>Zelle QR Code URL</Label>
              <Input
                type="url"
                value={zelleInfo.zelle_qr_code}
                onChange={(e) => setZelleInfo({ ...zelleInfo, zelle_qr_code: e.target.value })}
                placeholder="https://example.com/your-qr-code.png"
                className={isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}
                data-testid="zelle-qr-input"
              />
              <p className={`text-xs ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Upload your Zelle QR code image and paste the URL here. It will appear on printed invoices.
              </p>
              {zelleInfo.zelle_qr_code && (
                <div className="mt-3 space-y-2">
                  <img 
                    src={zelleInfo.zelle_qr_code} 
                    alt="Zelle QR Code Preview" 
                    className="w-24 h-24 object-contain border rounded"
                  />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleDeleteQrCode}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                      data-testid="delete-qr-btn"
                    >
                      <Trash2 className="w-3 h-3 mr-1" />
                      Remove QR
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      {/* Payment Providers Section - Full Width */}
      <Card className={`order-1 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
        <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <CreditCard className="w-5 h-5 text-green-600" />
            Payment Providers
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p className={`text-sm mb-6 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Connect your payment processors to automatically receive payments and update invoices. You can enable multiple providers.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Square */}
            <div className={`p-4 rounded-lg border-2 transition-all ${
              paymentProviders.square.enabled 
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20' 
                : isDark ? 'border-slate-600 bg-slate-700/50' : 'border-slate-200 bg-slate-50'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${paymentProviders.square.enabled ? 'bg-green-100 text-green-700' : isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                    <SquareLogo />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Square</h3>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Card reader & online</p>
                  </div>
                </div>
                <Switch
                  checked={paymentProviders.square.enabled}
                  onCheckedChange={() => toggleProvider('square')}
                  data-testid="square-toggle"
                />
              </div>
              {paymentProviders.square.enabled && (
                <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                  {paymentProviders.square.connected ? (
                    <div className="space-y-2" data-testid="square-connected-block">
                      <div className="flex items-center gap-2 text-green-600">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Connected · {paymentProviders.square.mode || 'live'} mode
                        </span>
                      </div>
                      {paymentProviders.square.location_id && (
                        <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'} truncate`} title={paymentProviders.square.location_id}>
                          Location: <code className="font-mono">{paymentProviders.square.location_id}</code>
                        </p>
                      )}
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setConnectingProvider('square')} className={isDark ? 'border-slate-600 text-slate-200' : ''} data-testid="square-update-keys-btn">
                          Update keys
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDisconnectProvider('square')} data-testid="square-disconnect-btn">
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Not connected yet. We'll open Square for you and walk you through the keys —
                        nothing saves until we verify it works.
                      </p>
                      <Button
                        onClick={() => setConnectingProvider('square')}
                        className="w-full bg-green-600 hover:bg-green-700 text-white"
                        data-testid="connect-square-btn"
                      >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Connect Square
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Stripe */}
            <div className={`p-4 rounded-lg border-2 transition-all ${
              paymentProviders.stripe.enabled 
                ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20' 
                : isDark ? 'border-slate-600 bg-slate-700/50' : 'border-slate-200 bg-slate-50'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${paymentProviders.stripe.enabled ? 'bg-purple-100 text-purple-700' : isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                    <StripeLogo />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Stripe</h3>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Online payments</p>
                  </div>
                </div>
                <Switch
                  checked={paymentProviders.stripe.enabled}
                  onCheckedChange={() => toggleProvider('stripe')}
                  data-testid="stripe-toggle"
                />
              </div>
              {paymentProviders.stripe.enabled && (
                <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                  {paymentProviders.stripe.connected ? (
                    <div className="space-y-2" data-testid="stripe-connected-block">
                      <div className="flex items-center gap-2 text-purple-600">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Connected · {paymentProviders.stripe.mode || 'live'} mode
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setConnectingProvider('stripe')} className={isDark ? 'border-slate-600 text-slate-200' : ''} data-testid="stripe-update-keys-btn">
                          Update keys
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDisconnectProvider('stripe')} data-testid="stripe-disconnect-btn">
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Connect Stripe in 30 seconds — we'll open the API keys page for you and validate
                        the keys before saving.
                      </p>
                      <Button
                        onClick={() => setConnectingProvider('stripe')}
                        className="w-full bg-violet-600 hover:bg-violet-700 text-white"
                        data-testid="connect-stripe-btn"
                      >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Connect Stripe
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* PayPal */}
            <div className={`p-4 rounded-lg border-2 transition-all ${
              paymentProviders.paypal.enabled 
                ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
                : isDark ? 'border-slate-600 bg-slate-700/50' : 'border-slate-200 bg-slate-50'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${paymentProviders.paypal.enabled ? 'bg-blue-100 text-blue-700' : isDark ? 'bg-slate-600 text-slate-300' : 'bg-slate-200 text-slate-600'}`}>
                    <PayPalLogo />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>PayPal</h3>
                    <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Online & invoicing</p>
                  </div>
                </div>
                <Switch
                  checked={paymentProviders.paypal.enabled}
                  onCheckedChange={() => toggleProvider('paypal')}
                  data-testid="paypal-toggle"
                />
              </div>
              {paymentProviders.paypal.enabled && (
                <div className="space-y-3 pt-3 border-t border-slate-200 dark:border-slate-600">
                  {paymentProviders.paypal.connected ? (
                    <div className="space-y-2" data-testid="paypal-connected-block">
                      <div className="flex items-center gap-2 text-blue-600">
                        <Check className="w-4 h-4" />
                        <span className="text-sm font-medium">
                          Connected · {paymentProviders.paypal.mode || 'live'} mode
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="outline" onClick={() => setConnectingProvider('paypal')} className={isDark ? 'border-slate-600 text-slate-200' : ''} data-testid="paypal-update-keys-btn">
                          Update keys
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => handleDisconnectProvider('paypal')} data-testid="paypal-disconnect-btn">
                          Disconnect
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                        Connect your PayPal app — we'll exchange your Client ID + Secret with PayPal
                        to confirm they work before saving.
                      </p>
                      <Button
                        onClick={() => setConnectingProvider('paypal')}
                        className="w-full bg-sky-600 hover:bg-sky-700 text-white"
                        data-testid="connect-paypal-btn"
                      >
                        <ShieldCheck className="w-4 h-4 mr-2" />
                        Connect PayPal
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Info Box */}
          <div className={`mt-6 p-4 rounded-lg flex items-start gap-3 ${isDark ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'}`}>
            <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
            <div>
              <p className={`text-sm font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>How it works</p>
              <p className={`text-sm mt-1 ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                When you connect a payment provider, all card payments will automatically update your invoices to "PAID". 
                Enable Square to use your card reader device for in-office payments or online checkout for remote payments.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
        </section>
        )}

        {/* ================= NOTIFICATIONS ================= */}
        {/* ================= NOTIFICATIONS + TAX ================= */}
        {mode !== 'payments' && (
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-0 items-start" data-testid="settings-section-messaging">
      <Card className={isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} data-testid="sms-card">
        <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
          <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <MessageSquare className="w-5 h-5 text-sky-500" />
            Notifications
            <Badge variant="outline" className="ml-2 text-[10px] border-amber-400 text-amber-500">COMING SOON</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
            Settings for invoice &amp; receipt notifications will live here. We'll let you know when it's ready.
          </p>
        </CardContent>
      </Card>

        {/* Tax Rates — collapsible accordion (closed by default).
            Lives in the right column next to Notifications so the page
            doesn't have lonely full-width strips. */}
        <details
          className={`group rounded-xl border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}
          data-testid="tax-rates-accordion"
        >
          <summary className={`flex items-center justify-between p-4 cursor-pointer select-none list-none ${isDark ? 'text-white' : 'text-slate-800'}`}>
            <div className="flex items-center gap-2.5">
              <Percent className="w-5 h-5 text-amber-500" />
              <div>
                <p className="text-base font-semibold">Tax Rates</p>
                <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                  {taxRates.length
                    ? `${taxRates.length} rate${taxRates.length === 1 ? '' : 's'} configured · ${taxRates.map(r => r === 0 ? 'No Tax' : `${r}%`).join(', ')}`
                    : 'No rates yet — add one to start charging tax on invoices'}
                </p>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 transition-transform duration-200 group-open:rotate-180" />
          </summary>
          <div className={`px-4 pb-4 pt-2 space-y-4 border-t ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex gap-2">
              <Input
                type="number"
                placeholder="Enter tax rate %"
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                className={`flex-1 ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-slate-50 border-slate-200'}`}
                data-testid="new-tax-rate-input"
              />
              <Button
                onClick={handleAddRate}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="add-tax-rate-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className={`flex flex-wrap gap-1.5 ${taxRates.length ? '' : 'hidden'}`}>
              {taxRates.map((rate) => (
                <span
                  key={rate}
                  className={`group/chip inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium ${isDark ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-700'}`}
                  data-testid={`tax-rate-chip-${rate}`}
                >
                  {rate === 0 ? "No Tax" : `${rate}%`}
                  <button
                    type="button"
                    onClick={() => handleRemoveRate(rate)}
                    className={`w-5 h-5 inline-flex items-center justify-center rounded-full opacity-50 hover:opacity-100 hover:text-red-500 transition-opacity`}
                    aria-label={`Remove ${rate}%`}
                    data-testid={`remove-rate-${rate}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>

            {taxRates.length === 0 && (
              <p className={`text-center py-3 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                No tax rates yet. Add one above.
              </p>
            )}

            {/* Labor + Default rates — inline panel (no nested accordion).
                Previously we wrapped this in a second collapsible which made
                the section feel deeper than it needed to be. Now it sits
                directly inside the Tax Rates accordion as a tight 2-col
                grid. */}
            <div
              className={`rounded-lg p-3 space-y-3 border ${isDark ? 'bg-slate-700/30 border-slate-600/60' : 'bg-blue-50/40 border-blue-200/70'}`}
              data-testid="state-tax-rules-accordion"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-blue-500" />
                  <div>
                    <p className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>Tax Labor Services</p>
                    <p className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                      {taxSettings.tax_labor ? 'Labor IS taxed' : 'Labor is NOT taxed'}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={taxSettings.tax_labor}
                  onCheckedChange={(checked) => setTaxSettings({ ...taxSettings, tax_labor: checked })}
                  data-testid="tax-labor-toggle"
                />
              </div>

              {/* Side-by-side Default + Labor rates so they share one row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className={`text-[11px] font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Default Tax Rate
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="0"
                      value={taxSettings.default_tax_rate || ''}
                      onChange={(e) => setTaxSettings({ ...taxSettings, default_tax_rate: parseFloat(e.target.value) || 0 })}
                      className={`h-8 pr-6 text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'}`}
                      data-testid="default-tax-rate-input"
                    />
                    <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>%</span>
                  </div>
                </div>
                <div>
                  <Label className={`text-[11px] font-medium ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                    Labor Tax Rate {!taxSettings.tax_labor && <span className="opacity-60">(off)</span>}
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      max="100"
                      placeholder="Same as parts"
                      value={taxSettings.labor_tax_rate ?? ''}
                      onChange={(e) => setTaxSettings({ ...taxSettings, labor_tax_rate: e.target.value === '' ? null : parseFloat(e.target.value) })}
                      disabled={!taxSettings.tax_labor}
                      className={`h-8 pr-6 text-sm ${isDark ? 'bg-slate-700 border-slate-600' : 'bg-white border-slate-200'} ${!taxSettings.tax_labor ? 'opacity-50' : ''}`}
                      data-testid="labor-tax-rate-input"
                    />
                    <span className={`absolute right-2 top-1/2 -translate-y-1/2 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>%</span>
                  </div>
                </div>
              </div>

              <p className={`text-[10px] leading-snug ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                Default rate is what applies to parts. Leave Labor Tax Rate blank to charge labor at the same rate.
              </p>
            </div>
          </div>
        </details>
        </section>
        )}
      </div>

      {/* Super Admin: Account Owners panel */}
      {isSuperAdmin && (
        <Card className={`mb-6 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`} data-testid="admin-account-owners-card">
          <CardHeader className={`border-b ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-800'}`}>
                <ShieldCheck className="w-5 h-5 text-fuchsia-500" />
                Account Owners <span className={`text-xs font-normal ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>(Super Admin only)</span>
              </CardTitle>
              <span className={`text-xs px-2.5 py-1 rounded-full ${isDark ? 'bg-slate-700/50 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
                {adminUsers.length} user{adminUsers.length === 1 ? '' : 's'}
              </span>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            {adminUsersLoading ? (
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>Loading users…</p>
            ) : adminUsers.length === 0 ? (
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>No users found for this business.</p>
            ) : (
              <div className="space-y-3">
                {adminUsers.map((u) => {
                  const role = ROLE_STYLES[u.role] || { label: u.role || 'User', color: 'text-slate-300', bg: 'bg-slate-500/10' };
                  return (
                    <div
                      key={u.id || u.email}
                      className={`flex flex-wrap items-center justify-between gap-3 p-4 rounded-lg border ${isDark ? 'bg-slate-700/30 border-slate-600/50' : 'bg-slate-50 border-slate-200'}`}
                      data-testid={`admin-business-user-${u.email}`}
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${role.bg}`}>
                          <Users className={`w-5 h-5 ${role.color}`} />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className={`font-semibold ${isDark ? 'text-white' : 'text-slate-800'}`}>{u.name || '(no name)'}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${role.bg} ${role.color}`}>{role.label}</span>
                            {u.email_verified === false && (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-500/10 text-amber-400">Email unverified</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                            <span className={`text-sm font-mono break-all ${isDark ? 'text-slate-300' : 'text-slate-600'}`}>{u.email}</span>
                          </div>
                          <div className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
                            Created: {formatDate(u.created_at)}
                            {u.last_login && <> &middot; Last login: {formatDate(u.last_login)}</>}
                            {u.id && <> &middot; ID: <span className="font-mono">{u.id.slice(0, 8)}…</span></>}
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => copyToClipboard(u.email, "Email")}
                          className={isDark ? 'border-slate-600 text-slate-200 hover:bg-slate-700' : ''}
                          data-testid={`copy-user-email-${u.email}`}
                        >
                          <Copy className="w-3.5 h-3.5 mr-1" /> Copy email
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => setResetPwTarget(u)}
                          className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white"
                          data-testid={`reset-password-btn-${u.email}`}
                        >
                          <Mail className="w-3.5 h-3.5 mr-1" /> Send reset email
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className={`text-xs mt-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}>
              To create or modify users for this business, go to <span className="font-medium">Admin → Users</span>.
              Passwords are never displayed for security.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Modals */}
      <LogoCropDialog
        open={!!cropSource}
        imageSrc={cropSource}
        onCancel={() => setCropSource(null)}
        onConfirm={handleCropConfirm}
      />
      <ResetPasswordDialog
        open={!!resetPwTarget}
        user={resetPwTarget}
        onClose={() => setResetPwTarget(null)}
      />
      <ConnectProviderDialog
        open={!!connectingProvider}
        provider={connectingProvider}
        businessSlug={businessSlug}
        initialValues={connectingProvider ? paymentProviders[connectingProvider] : {}}
        onConnected={handleProviderConnected}
        onClose={() => setConnectingProvider(null)}
      />
    </div>
  );
}
